import React, { useEffect, useRef, useState, useCallback } from "react";
import { CommonUtil } from "../../utils/CommonUtil";
import { Constants } from "../Languages/Constants";
import AGGrid from "../../Components/AGGrid";
import { NetworkManager } from "../../Components/NetworkManager";
import ToastManager from "../../Components/ToastManager";
import { Modal } from "react-bootstrap";
import CheckAvailabilityPreview from "./CheckAvailabilityPreview";
import CustomRedLoader from "../../Components/CustomRedLoader";

const MonthlyInvoices = ({ reservationId, showfor, ccid }: any) => {
  const gridRef = useRef<any>(null);
  const [invoiceColumnDefs, setInvoiceColumnDefs] = useState<any>([]);
  const [invoiceData, setInvoiceData] = useState<any>([]);
  const [invoiceHeaderData, setInvoiceHeaderData] = useState<any>({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const onLoginError = (err: any) => {
    setLoading(false);
    console.error(err);
  };

  const downloadPDF = useCallback(async (batchId: any, name: string) => {
    const orgId = CommonUtil.getSelectedOrgId();
    if (!orgId) return;

    const headers = new Headers({ "Content-Type": "application/json" });
    const token = localStorage.getItem("token");
    if (token) headers.append("Authorization", `Bearer ${token}`);

    const url =
      showfor === "statement"
        ? `api/download/vHubStatement?statementId=${batchId}`
        : `api/download/vHubInvoice?invoiceId=${batchId}`;

    try {
      const response = await fetch(`${NetworkManager.serverPath}${url}`, {
        method: "GET",
        headers,
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${name}.pdf`;
      link.click();
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  }, [showfor]);

  
    const onErrorbyPayment = (err: any) => {
      ToastManager.showToast(
        err?.errormessage
          ? err?.errormessage
          : "Something went wrong please try again.",
        "Failed"
      );
    };

    const retryCreditCardPayment = (invoiceId: any, ccId: any, isForRefund: any) => {
      if(!ccId){
        return ToastManager.showToast("Credit Card Not Found", "Error");
      }
      const getPaymentSuccessAPI: string = `api/reservation/card-payment/${invoiceId}?cardId=${ccId}&isRefund=${isForRefund === true ? true : false}`;
      NetworkManager.sendJsonRequest(getPaymentSuccessAPI, "", "", "POST", true, (resp: any) => onSuccessfullyPaid(resp), onErrorbyPayment);
    };
  
    const onSuccessfullyPaid = (resp: any) => {
      const newResp = JSON.parse(String(resp));
      if (newResp && newResp?.Status === "Payment Successful") {
        ToastManager.showToast( newResp?.Message, "Success");
      } else {
        ToastManager.showToast(newResp?.Message, "Error");
      }
      if(reservationId) {
        getMonthlyInvoices(reservationId);
      }
    };

  const getMonthlyInvoices = useCallback(
    (id: any) => {
      if (!id) return;
      setLoading(true);
      const apiUrl =
        showfor === "statement"
          ? `api/reservation/getMonthlyStatements?reservationId=${id}`
          : `api/reservation/getMonthlyInvoices?reservationId=${id}`;

      NetworkManager.sendJsonRequest(apiUrl, "", "", "GET", true, onSuccessMonthlyInvoices, onLoginError);
    },
    [showfor]
  );

  const onSuccessMonthlyInvoices = (response: any) => {
    setLoading(false);
    const restData = CommonUtil.parseData(response);
    if (!restData) return;

    const invoicePaymentStatus: any = [
      { field: "paymentStatus", headerName: "Payment Status" },
      {
        field: "Action",
        headerName: "Action",
        with: 150,
        cellRenderer: (params: any) => {
          const { paymentStatus, invoiceId, ccId, isForRefund } = params.data;
          const isFuture = invoiceId === 0 || invoiceId === 1 || !paymentStatus;

          if (isFuture) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <button
                  className="btn btn-sm btn-outline-info"
                  style={{ width: "80px", padding: "2px 5px", fontSize: "12px" }}
                  onClick={() => {
                    setSelectedInvoice(params.data)
                    setShowPreviewModal(true)
                  }}
                >
                  Preview
                </button>
              </div>
            );
          }

          const status = (paymentStatus || "").toLowerCase();
          if (status === "fully paid" || status === "refunded") {
            return null;
          }
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <button
                className="btn btn-sm btn-outline-primary"
                style={{ width: "80px", padding: "2px 5px", fontSize: "12px" }}
                onClick={() => retryCreditCardPayment(invoiceId, ccId, isForRefund)}
              >
                Retry
              </button>
            </div>
          );
        },
      },
    ];

    let invoiceCols = [
      { field: "billingDate", headerName: "Billing Date" },
      { field: "billStartDate", headerName: "Billing Start Date" },
      { field: "billEndDate", headerName: "Billing End Date" },
      {
        field: "invoiceCode",
        headerName: "Invoice Code",
        cellRenderer: (params: any) => (
          <div className="cellRenderHeader">
            <a>
              <span
                onClick={() =>
                  downloadPDF(params.data.invoiceId, params.data.invoiceCode)
                }
                className="cellRender"
              >
                {params.data.invoiceCode}
              </span>
            </a>
          </div>
        ),
      },
      ...(showfor !== "statement" ? invoicePaymentStatus : []),
      { field: "lastOdometerReading", headerName: "Last Odometer Reading" },
      { field: "lastEngineHours", headerName: "Last Engine Hours" },
      {
        field: "subTotalAmount",
        headerName: "Sub Total Amount",
        valueFormatter: (params: any) => params.value != null ? Number(params.value).toFixed(2) : ""
      },
      { field: "totalTaxes", headerName: "Total Taxes" },
      { field: "totalAmount", headerName: "Total Amount" },
    ].map((col) => {
      let width = 150;
      let flex = 0;

      if (col.field === "Action") {
        width = 110;
      } else if (col.field === "paymentStatus") {
        width = 100;
      } else if (["lastOdometerReading", "lastEngineHours"].includes(col.field)) {
        width = 100;
      } else if (col.field === "invoiceCode") {
        width = 130;
      }

      return {
        ...col,
        resizable: true,
        sortable: true,
        filter: "agTextColumnFilter",
        width,
        flex,
        minWidth: 70,
        suppressSizeToFit: col.field === "Action",
      };
    });

    const allInvoices = [
      ...(restData?.invoices || []),
      ...(restData?.futureInvoices || []),
    ];

    setInvoiceColumnDefs(invoiceCols);
    setInvoiceData(allInvoices);
    
    setInvoiceHeaderData({
      lastBilledAmount: restData.lastBilledAmount,
      lastBillingDate: restData.lastBillingDate,
      nextBillingDate: restData.nextBillingDate,
    });
  };

  useEffect(() => {
    if(reservationId)
      getMonthlyInvoices(reservationId);
  }, [reservationId]);

  const isStatement = showfor === "statement";
  const headerText = isStatement
    ? CommonUtil.getMessageText(Constants.SOI, "Statement of Income")
    : CommonUtil.getMessageText(Constants.MONTHLY_INVOICES, "Monthly Invoices");

  const subText = isStatement
    ? CommonUtil.getMessageText(
        Constants.ALL_STATEMENT_INVOICES,
        "All your statement will be added in this section"
      )
    : CommonUtil.getMessageText(
        Constants.ALL_YOUR_INVOICES,
        "All your invoices will be added in this section"
      );

  const labels = {
    lastAmount: isStatement ? "Last Statement Amount" : "Last Billed Amount",
    lastDate: isStatement ? "Last Statement Date" : "Last Billing Date",
    nextDate: isStatement ? "Next Statement Date" : "Next Billing Date",
  };

  return (
    <section style={{ position: 'relative' }}>
      <div className="row">
        <div className="col-sm-12">
          <h6>{headerText}</h6>
          <p>{subText}</p>
        </div>

        <div className="col-sm-12">
          <div className="row mb-2">
            <div className="col-4">
              <span>{`${labels.lastAmount}: ${invoiceHeaderData.lastBilledAmount || ""}`}</span>
            </div>
            <div className="col-4">
              <span>{`${labels.lastDate}: ${invoiceHeaderData.lastBillingDate || ""}`}</span>
            </div>
            <div className="col-4">
              <span>{`${labels.nextDate}: ${invoiceHeaderData.nextBillingDate || ""}`}</span>
            </div>
          </div>

          <div className="row">
            <div
              className="ag-theme-alpine gridHeight"
              style={{ height: 300, width: "100%", paddingLeft: 8, paddingRight: 5 }}
            >
              {loading ? (
                <CustomRedLoader />
              ) : (
                <AGGrid
                  id="OrgInvoiceGrid"
                  cols={invoiceColumnDefs}
                  rows={invoiceData}
                  pageSize={500}
                  headerHeight={CommonUtil.GRID_HEADER_HEIGHT} 
                  rowHeight={CommonUtil.GRID_ROW_HEIGHT}
                  ref={gridRef}
                  addNew={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal 
        show={showPreviewModal} 
        onHide={() => setShowPreviewModal(false)} 
        fullscreen 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {selectedInvoice && <CheckAvailabilityPreview data={selectedInvoice} />}
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default MonthlyInvoices;
